import { api } from './api';

const MEDIA_BASE_URL = (import.meta.env.VITE_MEDIA_BASE_URL as string)?.replace(/\/$/, '') || '';

interface MediaUrlsResponse {
  urls: Array<{ fileKey: string; url: string }>;
  expiresIn: number;
}

interface MediaObject {
  images?: string[];
  videos?: string[];
}

class MediaService {
  /**
   * Build display URL from file key. API returns keys; use this when displaying.
   */
  getMediaUrlFromKey(keyOrUrl: string): string {
    if (!keyOrUrl) return '';
    if (keyOrUrl.startsWith('http://') || keyOrUrl.startsWith('https://')) return keyOrUrl;
    if (!MEDIA_BASE_URL) return keyOrUrl;
    return `${MEDIA_BASE_URL}/${keyOrUrl}`;
  }

  async getMediaUrl(fileKey: string): Promise<string | null> {
    try {
      const response = await api.get(`/media/url`, { params: { fileKey } });
      if (response.data.success && response.data.data) return response.data.data.url;
      return null;
    } catch (error) {
      console.error('Error getting media URL:', error);
      return null;
    }
  }

  async getBatchMediaUrls(fileKeys: string[]): Promise<Map<string, string>> {
    try {
      const response = await api.post<{ success: boolean; data: MediaUrlsResponse }>('/media/batch-urls', {
        fileKeys,
      });
      if (response.data.success && response.data.data) {
        const urlMap = new Map<string, string>();
        response.data.data.urls.forEach((item: { fileKey: string; url: string }) => {
          urlMap.set(item.fileKey, item.url);
        });
        return urlMap;
      }
      return new Map();
    } catch (error) {
      console.error('Error getting batch media URLs:', error);
      return new Map();
    }
  }

  /**
   * Convert media object (file keys) to display URLs using domain + key.
   */
  async convertMediaToUrls(media: MediaObject): Promise<MediaObject> {
    try {
      const allFileKeys: string[] = [];
      if (media.images) allFileKeys.push(...media.images);
      if (media.videos) allFileKeys.push(...media.videos);
      if (allFileKeys.length === 0) return media;
      return {
        images: media.images?.map((key) => this.getMediaUrlFromKey(key)),
        videos: media.videos?.map((key) => this.getMediaUrlFromKey(key)),
      };
    } catch (error) {
      console.error('Error converting media to URLs:', error);
      return media;
    }
  }

  /**
   * Extract fileKey from a full URL (path after domain).
   */
  extractFileKey(url: string): string {
    // If it's already a fileKey (doesn't start with http), return as-is
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return url;
    }

    // Try to extract from CloudFront URL
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      // Remove leading slash
      return pathname.startsWith('/') ? pathname.slice(1) : pathname;
    } catch {
      return url;
    }
  }
}

export const mediaService = new MediaService();
